import { FC } from 'react';
import { Link } from 'react-router-dom';
import { State } from 'redux/initialState';
import { useSelector } from 'react-redux';

import { HoldingClass, Partner } from 'types';
import TableRow from 'modules/_shared/Table/components/TableRow';
import Button from 'modules/_shared/components/Buttons';

import { formatNumber } from 'utils/formats';
import { useOperationDetailsPage } from '../../context/OperationDetailsPageContext';
import { useGetRenumerationDataForPartner } from '../../hooks/useGetRenumerationDataForPartner';
import { buildCells } from '../../utils/buildRenumerationCells';

type Range = {
  class: string | HoldingClass;
  from: number;
  to: number;
};

type RenumerationOperationDataRowProps = {
  index: number;
  partner: Partner;
  isExpanded: boolean;
  onToggleExpand: (partnerId: string) => void; // eslint-disable-line no-unused-vars
};

const getClassName = (range: Range): string =>
  (range.class as HoldingClass)?.name ?? '-';

const getRangeShares = (range: Range): number => range.to - range.from + 1;

const RenumerationOperationTableRow: FC<RenumerationOperationDataRowProps> = ({
  index,
  partner,
  isExpanded,
  onToggleExpand,
}) => {
  const actualSociety = useSelector(
    (state: State) => state.society.actualSociety
  );
  const { data } = useOperationDetailsPage();
  const { _id: partnerId, name: partnerName } = partner;

  const { data: renumerationData } = useGetRenumerationDataForPartner({
    renumerationOperation: data.operation,
    partner,
  });

  const { rangeByClass = [] } = renumerationData;
  const hasRanges = rangeByClass.length > 0;
  const isMultiRange = rangeByClass.length > 1;

  const partnerLink = (
    <Link
      className="text-primary"
      to={`/detalle-socio/${actualSociety?._id}/${partnerId}`}
    >
      {partnerName}
    </Link>
  );

  if (!isMultiRange) {
    const range = rangeByClass[0];

    const cells = buildCells({
      index,
      expandContent: <span />,
      partnerContent: partnerLink,
      className: hasRanges ? getClassName(range) : '-',
      from: hasRanges ? formatNumber(range.from) : '-',
      to: hasRanges ? formatNumber(range.to) : '-',
      shares: hasRanges ? formatNumber(getRangeShares(range)) : '-',
    });

    return <TableRow cells={cells} />;
  }

  const totalShares = rangeByClass.reduce(
    (sum, range) => sum + getRangeShares(range),
    0
  );

  const parentCells = buildCells({
    index,
    expandContent: (
      <Button
        className="badge btn-light"
        onClick={() => onToggleExpand(partnerId)}
      >
        <em className={`icon ni ni-chevron-${isExpanded ? 'up' : 'down'}`} />
      </Button>
    ),
    partnerContent: partnerLink,
    className: '-',
    from: '-',
    to: '-',
    shares: formatNumber(totalShares),
  });

  return (
    <>
      <TableRow cells={parentCells} />

      {isExpanded &&
        rangeByClass.map((range, rangeIndex) => (
          <TableRow
            // eslint-disable-next-line react/no-array-index-key
            key={`range-${partnerId}-${rangeIndex}`}
            className="bg-light inner-shadow"
            cells={buildCells({
              index: `${index}-${rangeIndex}`,
              expandContent: <span />,
              partnerContent: '',
              className: getClassName(range),
              from: formatNumber(range.from),
              to: formatNumber(range.to),
              shares: formatNumber(getRangeShares(range)),
            })}
          />
        ))}
    </>
  );
};

export default RenumerationOperationTableRow;
